/**
 * The pointers on a canvas, and what their moves mean: a drag or a pinch (D340).
 *
 * Both canvases were written for one pointer. A second finger overwrote the
 * first one's id and last position, every move was then measured against
 * whichever finger had moved last, and the view jumped back and forth by the
 * distance between them; the first finger up ended the gesture for both, and a
 * first finger that had landed on a node fed that jitter into its stretch. The
 * stylesheet's `touch-action: none` — which has to stay, or the page itself
 * zooms — had turned the browser's own pinch off, so on a phone the only zoom
 * was the dock's buttons and the most familiar gesture on a map broke the map.
 *
 * So each pointer is kept by its own id, and a move is measured against that
 * pointer's own last position, never another's:
 *
 *   - The first pointer down LEADS. Its moves are a drag, which each canvas reads
 *     as it always has: a pan or a stretch on the flat one, an orbit on the
 *     spatial one.
 *   - A second pointer makes the gesture a PINCH, and it stays one until every
 *     pointer is up (`pinched`). A move of either finger in the pair is a scale
 *     — the ratio of the span between them to the span a move ago — about their
 *     last midpoint, and a translation by how far that midpoint moved. Zooming
 *     about the old midpoint and then moving by the midpoint's travel is what
 *     keeps the world point between the fingers under the fingers.
 *   - A third pointer is kept, so that lifting one of the pair can re-pair the
 *     two left, but moves nothing while the pair stands.
 *   - A pointer whose up never arrived is not kept for ever: the same id landing
 *     again lifts it first, and each canvas sweeps out, before every landing,
 *     the ids it no longer holds a capture for (`ids`). A ghost left behind would
 *     turn every later one-finger press into a pinch.
 *   - When a pinch is down to one finger, that finger leads again from where it
 *     IS, so lifting one finger moves nothing and the one left drags on without a
 *     jump. It drags the view only: a pinch never resolves a tap and never arms
 *     a stretch, which each canvas reads off `pinched`.
 *
 * Pure: no DOM and no store. Positions are whatever coordinates the caller
 * passes in (the canvases pass client coordinates and convert the midpoint).
 */

export interface Pt {
  x: number
  y: number
}

/** What one move means. `null` from `move` means it means nothing. */
export type Step =
  | { kind: 'drag'; dx: number; dy: number }
  | {
      kind: 'pinch'
      /** Span now over span a move ago: above 1 is the fingers parting. */
      factor: number
      /** The midpoint a move ago — the point the scale is about. */
      cx: number
      cy: number
      /** How far the midpoint moved since. */
      dx: number
      dy: number
    }

/**
 * What a pointer landing did: it began the gesture, it turned the gesture into
 * a pinch, or it joined one that already was.
 */
export type Landing = 'lead' | 'pinch' | 'extra'

/**
 * A span below this many pixels is not a span. Two fingertips cannot be this
 * close, so it only ever guards a degenerate report — and dividing by it would
 * zoom the view to its clamp in one move.
 */
export const MIN_SPAN = 1

export class Pointers {
  private at = new Map<number, Pt>()
  private lead: number | null = null
  private pair: [number, number] | null = null
  private span = 0
  private mid: Pt = { x: 0, y: 0 }
  /** A second pointer joined this gesture. Holds until every pointer is up. */
  pinched = false

  /** How many pointers are down. */
  get size(): number {
    return this.at.size
  }

  has(id: number): boolean {
    return this.at.has(id)
  }

  /** The ids of the pointers down, so a canvas can sweep out the ones it lost. */
  ids(): number[] {
    return [...this.at.keys()]
  }

  down(id: number, x: number, y: number): Landing {
    // A pointer cannot land twice. One that does lost its up somewhere — a
    // mouse's ⌃-click whose context menu swallowed the mouseup — and keeping the
    // stale entry would make this press 'extra', so it would skip the lead's
    // reset and drag whatever the lost press had armed. It is lifted first and
    // lands as new.
    if (this.at.has(id)) this.up(id)
    if (this.at.size === 0) {
      this.pinched = false
      this.pair = null
      this.lead = id
      this.at.set(id, { x, y })
      return 'lead'
    }
    this.at.set(id, { x, y })
    if (this.pair) return 'extra'
    // The second pointer down. Whatever the first was doing — a pan, a stretch
    // it armed, a tap it was about to be — the gesture is a pinch from here on.
    const other = this.lead ?? this.firstOtherThan(id)
    this.lead = null
    this.pinched = true
    this.seat(other, id)
    return 'pinch'
  }

  move(id: number, x: number, y: number): Step | null {
    const was = this.at.get(id)
    if (!was) return null
    this.at.set(id, { x, y })
    if (this.pair) {
      if (this.pair[0] !== id && this.pair[1] !== id) return null
      const a = this.at.get(this.pair[0])!
      const b = this.at.get(this.pair[1])!
      const span = Math.hypot(b.x - a.x, b.y - a.y)
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      const factor = span >= MIN_SPAN && this.span >= MIN_SPAN ? span / this.span : 1
      const step: Step = {
        kind: 'pinch',
        factor,
        cx: this.mid.x,
        cy: this.mid.y,
        dx: mid.x - this.mid.x,
        dy: mid.y - this.mid.y,
      }
      this.span = span
      this.mid = mid
      return step
    }
    if (id !== this.lead) return null
    return { kind: 'drag', dx: x - was.x, dy: y - was.y }
  }

  /**
   * Lifts a pointer. True when it was the last one: the gesture is over.
   * `pinched` outlives the last lift, because that lift is exactly when a canvas
   * asks whether this was a tap; the next gesture's first pointer resets it.
   */
  up(id: number): boolean {
    if (!this.at.delete(id)) return false
    if (this.at.size === 0) {
      this.lead = null
      this.pair = null
      this.span = 0
      return true
    }
    if (this.pair && (this.pair[0] === id || this.pair[1] === id)) {
      const left = [...this.at.keys()]
      if (left.length >= 2) {
        // Re-paired from where the fingers are now, so the new pair's first
        // move is measured against itself and not against the finger that left.
        this.seat(left[0], left[1])
      } else {
        this.pair = null
        this.lead = left[0]
      }
    } else if (id === this.lead) {
      this.lead = null
    }
    return false
  }

  private seat(a: number, b: number) {
    this.pair = [a, b]
    const pa = this.at.get(a)!
    const pb = this.at.get(b)!
    this.span = Math.hypot(pb.x - pa.x, pb.y - pa.y)
    this.mid = { x: (pa.x + pb.x) / 2, y: (pa.y + pb.y) / 2 }
  }

  private firstOtherThan(id: number): number {
    for (const k of this.at.keys()) if (k !== id) return k
    return id
  }
}
